import g from '#core/global';
import load from '#core/load';
import layout from '#core/layout';
import events from '#core/events';
import util from '#core/util';
import { scroll_lock } from '#core/pattern-params';


const sticky_item_$ = '.scroll-lock__item';
const sticky_patterns_$ = '.scroll-lock__item__patterns--sticky';

const active_c = 'scroll-lock__item--active';
const inactive_c = 'scroll-lock__item--inactive';
const enter_c = 'scroll-lock__item--enter';
const leave_c = 'scroll-lock__item--leave';

const layout_switch = scroll_lock.layout_switch;

let has_scroll_lock = false;
let $sticky_items = null;
let previous_active_index = null;


const module = {

	/**
	 * Init module
	 */
	init() {
		if (load.ran('scroll-lock')) return false;

		load.content(() => {
			has_scroll_lock = assign_elements();
			if (!has_scroll_lock) return false;
		});
		load.all(() => {
			has_scroll_lock = assign_elements();
			if (!has_scroll_lock) return false;

			assign_scroll_events();
			update_active_index();
		});
	},

};

export default module;
module.init();


/**
 * Assign elements and event listeners
 * @return {boolean}
 */
function assign_elements() {
	if (has_scroll_lock) return true;

	$sticky_items = document.querySelectorAll(sticky_item_$);
	if (!$sticky_items.length) return false;

	return true;
}


/**
 * Assign scroll events
 */
function assign_scroll_events() {
	window.addEventListener(g.scroll_e, util.throttle(() => {
		update_active_index();
	}, 250));
}


/**
 * Update active index
 */
function update_active_index() {
	// On mobile, keep first item active without scroll-triggered animations
	if (!layout.isBreakpointMin(layout_switch)) {
		if (previous_active_index === null) {
			$sticky_items[0]?.classList.remove(inactive_c);
			$sticky_items[0]?.classList.add(active_c);
			previous_active_index = 0;
		}
		return;
	}

	let new_active_index = -1;

	$sticky_items.forEach(($item, index) => {
		const is_intersecting = layout.isIntersecting({
			$element: $item,
			top_only: index === $sticky_items.length - 1,
		});
		if (is_intersecting) {
			new_active_index = index;
		}
	});

	// Default to first item if none intersecting
	if (new_active_index === -1) {
		new_active_index = 0;
	}

	// Only update if active index changed
	if (new_active_index === previous_active_index) {
		return;
	}

	// Handle leaving item
	if (previous_active_index !== null) {
		const $leaving_item = $sticky_items[previous_active_index];
		const $leaving_sticky = $leaving_item.querySelector(sticky_patterns_$);

		$leaving_item.classList.remove(active_c);
		$leaving_item.classList.add(inactive_c, leave_c);

		$leaving_item.dispatchEvent(new CustomEvent(events.scroll_lock.leave, {
			bubbles: true,
			detail: {
				index: previous_active_index,
			},
		}));

		// Remove leave class when the sticky pattern finishes transitioning.
		// Fall back to next frame if nothing transitions.
		const cleanup_leave = () => {
			$leaving_item.classList.remove(leave_c);
		};

		if ($leaving_sticky) {
			let handled = false;
			$leaving_sticky.addEventListener(g.transition_end_e, function done() {
				if (handled) return;
				handled = true;
				$leaving_sticky.removeEventListener(g.transition_end_e, done);
				cleanup_leave();
			});
			// Safety: if no transition fires, clean up on the next frame.
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					if (!handled) {
						handled = true;
						cleanup_leave();
					}
				});
			});
		} else {
			cleanup_leave();
		}
	}

	// Handle entering item
	const $entering_item = $sticky_items[new_active_index];
	const $entering_sticky = $entering_item.querySelector(sticky_patterns_$);

	const activate_item = () => {
		$entering_item.classList.remove(inactive_c, leave_c);
		$entering_item.classList.add(active_c, enter_c);

		$entering_item.dispatchEvent(new CustomEvent(events.scroll_lock.enter, {
			bubbles: true,
			detail: {
				index: new_active_index,
			},
		}));

		// Remove enter class after transition completes
		$entering_sticky?.addEventListener(g.transition_end_e, function done() {
			$entering_sticky.removeEventListener(g.transition_end_e, done);
			$entering_item.classList.remove(enter_c);
		});
	};

	// For initial page load, ensure inactive state is painted before activating
	if (previous_active_index === null) {
		requestAnimationFrame(() => {
			requestAnimationFrame(activate_item);
		});
	} else {
		activate_item();
	}

	previous_active_index = new_active_index;
}
